<template>
  <div class="ged-list">

    <div
      v-hide="
        fetchState.status.value !== 'success' ||
        providerCallCount === 0
      "
      class="ged-list__subfilters"
    >
      <ui-wad class="font-weight-light font-size-90">
        <span
          v-if="context.total > 0"
        >
          <layout-misc-float :value="context.total" />
          {{ context.total <= 1 ? 'résultat' : 'résultats' }}
        </span>
        <span
          v-if="context.total === 0"
        >
          Aucun résultat
        </span>
      </ui-wad>

      <ui-button-link
        v-if="hasAnyFilter"
        :size="UI.SIZE.SMALL"
        :variant="UI.VARIANT.SECONDARY"
        class="font-size-90"
        underline="hover"
        @click="resetFilters"
      >
        <ui-icon
          name="filter_alt_off"
        />
        supprimer les filtres
      </ui-button-link>
    </div>

    <b-card
      :class="[
        'ged-list__filters',
        (
          fetchState.status.value !== 'success' ||
          providerCallCount === 0
        ) ? 'ged-list__filters--busy' : '',
      ]"
      body-class="p-1"
    >
      <ged-filters
        :filters="context.filters"
        :salaries="selectedSalaries"
        :update-filters-callback="setFilters"
        :load-salarie-options-callback="loadSalarieOptions"
        :filter-options-callback="filterSalaries"
        :select-salarie-callback="selectSalarie"
        :remove-salarie-callback="removeSalarie"
        :document-type-options="documentTypeOptions"
        :year-options="yearOptions"
      />
    </b-card>

    <ui-table-wrapper
      ref="table"
      :provider="provider"
      :fields="fields"
      :initial-sort-by="context.order"
      :initial-sort-desc="context.sort === 'desc'"
      primary-key="document_id"
      :size="UI.SIZE.SMALL"
      :tbody-tr-class="tbodyRowClass"
      hover
      striped
      fixed
      @row-clicked="({ item }) => openDocumentPanel(item)"
    >
      <template v-if="providerCallCount === 0" #table-busy>
        <layout-placeholder-table-body
          :count="context.perPage"
          :size="UI.SIZE.SMALL"
          striped
        />
      </template>

      <template #head(actions)>
          <ui-button-icon
            :size="UI.SIZE.SMALL"
            :variant="UI.VARIANT.DARK"
            :name="isPageSelected ?
              'check_box' :
              'check_box_outline_blank'
            "
            @click="togglePageSelect"
          />
      </template>

      <template #cell(actions)="data">
        <div class="ged-list__actions__inner">
          <ui-button-icon
            :size="UI.SIZE.SMALL"
            :variant="UI.VARIANT.DARK"
            :name="isItemSelected(data.item) ?
              'check_box' :
              'check_box_outline_blank'
            "
            @click="toggleItem(data.item)"
          />

        </div>
      </template>

      <template #cell(fullname)="props">
        <span>
          {{ props.item.fullname }}
          <span class="color-gray-deep font-weight-light">
            &dash; {{ props.item.matricule }}
          </span>
        </span>
      </template>

      <template #cell(period)="props">
        <div
          :class="[
            'ged-list__period',
            isDatetimeValid(
              { inputFormat: 'DD/MM/YYYY' },
              props.unformatted
            ) ? 'ged-list__period--date' : ''
          ]">
            {{ props.value }}
          </div>
      </template>

      <template #cell(type)="props">
        <ui-badge-light
          :class="`ged-list__type ged-list__type--${props.item.type}`"
        >
          {{ getDocumentLabel(props.item.type) }}
        </ui-badge-light>
      </template>
    </ui-table-wrapper>

    <div class="ged-list__footer">

      <ui-table-pagination
        v-model="context.currentPage"
        :total-rows="context.total"
        :per-page="context.perPage"
      />

      <div class="ged-list__footer__per-page">
        <div class="ged-list__footer__per-page__text">
          par page
        </div>
        <ui-form-select
          v-model="context.perPage"
          :options="perPageOptions"
          :size="UI.SIZE.SMALL"
          class="ged-list__filters__per-page"
        />
      </div>

    </div>

    <teleport defer to="[data-teleport='footer']">
      <div
        :class="[
          'ged-list__action-footer',
        ]"
      >

        <transition
          enter-active-class="animate__animated animate__slideInUp ease-in-out a1 d1"
          leave-active-class="animate__animated animate__slideOutDown ease-in-out a1"
        >
          <div
            v-if="selectedItems.length"
            class="ged-list__action-footer__inner"
          >

            <ui-wad class="font-weight-bolder font-size-80 ml-4 mr-4 text-uppercase">
              Actions groupées
            </ui-wad>

            <ui-pending-icon
              ref="pending"
              class="mr-2.5"
              :size="UI.SIZE.MEDIUM"
            />

            <ui-button
              outline
              class="px-3.5"
              data-cy="download_multiple_button"
              :size="UI.SIZE.MEDIUM"
              :variant="UI.VARIANT.SECONDARY"
              :status="isDownloadPending ? UI.STATUS.DEFFERED : UI.STATUS.ENABLED"
              @click="() => downloadMultipleDocuments(selectedItems)"
            >
              <ui-icon
                name="file_download"
              />
              Télécharger
            </ui-button>

            <ui-button
              outline
              class="ml-3.5 px-3.5"
              data-cy="preview_multiple_button"
              :size="UI.SIZE.MEDIUM"
              :variant="UI.VARIANT.SECONDARY"
              @click="() => showMultipleDocuments(selectedItems)"
            >
              <ui-icon
                name="visibility"
              />
              Prévisualiser
            </ui-button>

            <counter
              :count="selectedItems.length"
              :print="count => count > 1 ?
                `documents sélectionnés` :
                `document sélectionné`
              "
              class="ml-6 mr-6 text-transform-uppercase font-weight-bolder font-size-80 line-height-2.0"
            />

            <ui-wad class="d-flex align-items-center gap-2">
              <ui-button-link
                :variant="UI.VARIANT.SECONDARY"
                class="font-size-90"
                underline="hover"
                bulky
                @click="unselectAll"
              >
                <ui-icon name="clear" />
                Enlever toute ma sélection
              </ui-button-link>
            </ui-wad>

          </div>
        </transition>

      </div>
    </teleport>


    <!-- DOCUMENT INSPECTION SIDEBAR -->
    <ui-sidebar
      :id="GED_DOCUMENT_SIDEBAR_ID"
      v-model="displayDocumentSidebar"
      :size="UI.SIZE.SMALL"
      class="ged-list__sidebar"
      placement="end"
      shadow
      lazy
      teleport-disabled
      no-header
      fit-workspace
      focus=".document-organism__top-section__thumbnail__button"
    >
      <document-summary-organism
        v-if="displayDocumentSidebar"
        :document="currentDocument"
        @close="displayDocumentSidebar = false"
      />
    </ui-sidebar>

    <!-- DOCUMENT INSPECTION SIDEBAR -->

    <!-- DOCUMENT PREVIEW -->
    <ui-modal
      :id="SINGLE_DOCUMENT_PREVIEW_MODAL_ID"
      no-header
      no-footer
      body-class="p-0"
      no-animation
      focus=".ged-single-document-viewer__container__header__actions"
    >
      <single-document-viewer
        :file-information="fileInformation"
        :document-id="currentDocument.document_id"
        @close="hideSingleDocumentViewer"
      />
    </ui-modal>
    <!-- DOCUMENT PREVIEW -->

    <!-- DOCUMENT MULTI PREVIEW -->
    <multi-document-viewer
      v-if="displayMultiDocumentModal"
      :documents="selectedDocuments"
      @close="displayMultiDocumentModal = false"
    />
    <!-- DOCUMENT MULTI PREVIEW -->


  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/table/members
@import ~/assets/sass/modules/form/variables
@import ~/assets/sass/modules/ged/members

$actions-column-width: 6vw

.ged-list
  $self: &

  padding-top: $layout-spacer-y * .75
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y
  flex-grow: 1

  &__subfilters
    display: flex
    gap: $card-spacer-x
    align-items: baseline
    margin-bottom: $layout-spacer-y * .25
    padding-left: space-x(1.5)
    padding-right: space-x(1.5)
    animation: fade-in 150ms ease-in-out

  &__filters
    display: flex
    margin-bottom: $layout-spacer-y

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

  &__action-footer
    position: sticky
    width: 100%
    bottom: 0
    left: 0

    &__inner
      background-color: $background-primary
      padding: $card-spacer-y $card-spacer-x
      border-top: 1px solid $border-color
      box-shadow: $shadow-discreet
      display: flex
      align-items: center
      // ("Law of Separation" between footer and lateral menu)
      border-left: 1px solid $gray-97

  &__footer
    margin-top: $layout-spacer-y
    display: flex
    align-items: center
    justify-content: center
    justify-items: center

    .ui-table-pagination
      margin-left: auto
      margin-right: auto

    &__per-page
      display: flex
      align-items: center
      gap: space-x(1)
      flex-shrink: 0

      &__text
        font-weight: $font-weight-light
        font-size: 90%
        white-space: nowrap

  //////////////////////////

  :deep(.b-table.table)
    @include table-presets

    /// Columns
    #{ $self }__actions
      width: $actions-column-width
      padding-left: space-x(1)

    /// Cells
    tbody

      #{ $self }__actions
        @include table-cell-presets
          gap: space-x(1)

      #{ $self }__period
        &:not(th)
          font-size: 90%
          color: $gray-deep

        &--date
          &:not(th)
            font-size: 90%
            word-spacing: 0.05em // to avoid the "word-spacing" issue with the date format DD/MM/YYYY

      #{ $self }__last-modification

        &:not(th)
          font-size: 85%
          color: $gray-deep

      #{ $self }__type
        @include document-type()

///////////////////////////////

// @XXX hack to put the modal above the sidebar
// we do not have control over the z-index of the modal because it is set
// internally by the offcanvas component.
// HOWEVER, we do have control over the z-index of the offcanvas component
// and its backdrop we can use the variables provided by bootstrap
// to set the z-index of the offcanvas component and its backdrop.
// Becaause the z-index of the modals seems to auto increment
// we offset the z-index by a large amount to ensure the modal is always on top

:deep()
  .offcanvas
    z-index: #{ $zindex-modal - 10 }

  .offcanvas-backdrop
    z-index: #{ $zindex-modal - 11 }

</style>

<script lang="ts">
import GedFilters from '~/components/ged/document/list/Filters.vue'
import { useCompanyOptionalNavigationContext } from '~/composables/layout/navigation'
import { useToastBuilder } from '~/composables/layout/toast'
import { isDatetimeValid, parseDatetime } from '~/utils/lib/core/datetime'
import { any, clone, concat, equals, map, pipe_, prop, reject, tapCatch, toString } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { getDocumentLabel } from '~/stores/ged'
import { downloadFromChunks, REMOTE_ERRORS, useStore_ged_documents_download } from '~/stores/ged/documents/download'
import DocumentSummaryOrganism from '~/components/ged/document/summary/Organism.vue'
import SingleDocumentViewer from '~/components/ged/document/viewer/SingleDocumentViewer.vue'
import MultiDocumentViewer from '~/components/ged/document/viewer/MultiDocumentViewer.vue'
import type { DocumentIdVersion, GedDetailedDocument } from '~/stores/ged/documents/details'
import { anyFilterSet, documentListFiltersBoilerplate, useStore_ged_documents_list, type GedDocumentListFilters } from '~/stores/ged/documents/list'
import type { GedDocument } from '~/stores/ged'
import { useStore_ged_documents_preview, type FileInformation } from '~/stores/ged/documents/preview'
import { useLegacyModalController } from '~/composables/layout/modal'
import Console from '~/utils/lib/core/console'
import { useStore_ged_salarie, type GedSalarie } from '~/stores/ged/salarie'
import { ERRORS } from '~/utils/lib/core/download'
import type { UiPendingIcon, UiTableWrapper } from '#components'
import { useTableProvider, type DefaultData as ProviderDefaultData } from '~/composables/layout/table-provider'
import { wrap as wrapInput } from '~/utils/infra/common/types'
import Counter from '~/components/layout/misc/Counter.vue'
import { useTableSelectionMultipleLight } from '~/composables/layout/table-selection-multiple-light'
import { toString as toString_ } from '~/utils/lib/core/float'
import { useLegacyPageFetch } from '~/composables/core/hook'
import type { UITreeOption } from '~/components/ui/form/Tree.vue'
import type { ComponentExposed } from 'vue-component-type-helpers'

const GED_DOCUMENT_SIDEBAR_ID = 'ged-document-list-inspection-sidebar'
const SINGLE_DOCUMENT_PREVIEW_MODAL_ID = 'single-document-preview-modal'

const console = new Console('component:ged-list')

export type download_document_cbk = (document_id: GedDetailedDocument['document_id'], version: DocumentIdVersion) => Promise<void>
export type preview_document_cbk = (document: GedDetailedDocument['document_id'], version: DocumentIdVersion) => Promise<void>

export default defineNuxtComponent({
  components: {
    GedFilters,
    DocumentSummaryOrganism,
    SingleDocumentViewer,
    MultiDocumentViewer,
    Counter,
  },
  provide() {
    return {
      'getDocumentPreview': this.getDocumentPreview,
      'downloadDocument': this.downloadDocument,
    }
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_OPTIONAL,
      menuItem: MENU_ITEM.DOCUMENTS,
    })
    const navigationContext = useCompanyOptionalNavigationContext()

    useHead(() => {
      const currentLevel = navigationContext.fromCurrentLevel()

      switch (currentLevel.kind) {
        case 'COMPANY':
          return {
            title: `Liste des documents — ${currentLevel.company.raison_sociale}`,
          }
        case 'STRUCTURE':
          return {
            title: `Liste des documents — ${currentLevel.structure.raison}`,
          }
        default:
          return {
            title: 'Liste des documents',
          }
      }
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const {
      hideModal,
      showModal,
    } = useLegacyModalController()

    const ToastBuilder = useToastBuilder()

    const ref_table
      = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')

    const ref_pending_icon
      = useTemplateRef<InstanceType<typeof UiPendingIcon>>('pending')

    const providerDefaultData :ProviderDefaultData = {
      sustainPhaseDuration: 300,
      context: {
        perPage: 50,
        order: 'last_modification',
        sort: 'desc',
        filters: clone(documentListFiltersBoilerplate),
      }
    }

    const tableProvider = useTableProvider<GedDocument, GedDocumentListFilters>(
      ref_table,
      providerDefaultData
    )

    const tableSelectionMultiple = useTableSelectionMultipleLight<GedDocument>(
      tableProvider.currentItems,
      (item, item_) => {
        const id  = prop('document_id', item)
        const id_ = prop('document_id', item_)
        return equals(id, id_)
      },
    )

    const store_documents_list = useStore_ged_documents_list()
    const store_documents_preview = useStore_ged_documents_preview()
    const store_documents_download = useStore_ged_documents_download()
    const store_salarie = useStore_ged_salarie()

    return {
      ...navigationContext,
      ToastBuilder,
      hideModal,
      showModal,
      fetchState,
      fetchWrapper,
      ...tableProvider,
      ...tableSelectionMultiple,
      store_documents_list,
      store_documents_preview,
      store_salarie,
      store_documents_download,
      ref_pending_icon,
    }
  },
  data() {
    return {
      isDatetimeValid,
      getDocumentLabel,
      GED_DOCUMENT_SIDEBAR_ID,
      SINGLE_DOCUMENT_PREVIEW_MODAL_ID,
      isDownloadPending: false,
      fields: [
        {
          key: 'actions',
          label: '',
          class: 'ged-list__actions'
        },
        {
          key: 'nom_abrege',
          label: 'Societe',
          tdClass: 'font-size-85'
        },
        {
          key: 'fullname',
          label: 'Nom complet - Matricule',
          sortable: true,
        },
        {
          key: 'period',
          label: 'Période',
          sortable: true,
        },
        {
          key: 'type',
          label: 'Type',
        },
        {
          class: 'ged-list__last-modification',
          key: 'last_modification',
          label: 'Dernière modification',
          formatter: ({ value }) => pipe_(
            value,
            parseDatetime(
              {
                inputFormat: undefined,
                outputFormat: 'DD/MM/YY — HH:mm'
              }
            ),
          ),
          sortable: true,
        },
      ],
      perPageOptions: [
        {
          value: 50,
          text: '50'
        },
        {
          value: 100,
          text: '100',
        },
        {
          value: 200,
          text: '200'
        }
      ],
      selectedSalaries: [] as GedSalarie[],
      displayDocumentSidebar: false,
      currentDocument: null as unknown as GedDocument,
      fileInformation: null as unknown as FileInformation,
      displayMultiDocumentModal: false,
      selectedDocuments: [] as GedDocument[],
      documentTypeOptions: [] as UITreeOption[],
      yearOptions: [] as UITreeOption[],
    }
  },
  computed: {
    hasAnyFilter() {
      return anyFilterSet(this.context.filters)
    },
    isPageSelected() {
      return this.selectedItems.length && this.currentItems.length
    },
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )

    this.consumer = (context) => {
      const {
        structure: { uuidData },
        company_code_list,
        current_company_code,
      } = this.getGedScope()

      const options = {
        input: wrapInput(context),
        uuidData,
        code_societe: current_company_code,
        code_societes: company_code_list,
      }

      return this.store_documents_list.exec(options)
    }
  },
  methods: {
    toString_,
    togglePageSelect() {
      if (this.isPageSelected) {
        this.unselectAll()
      } else {
        this.selectAll()
      }
    },
    resetFilters() {
      this.selectedSalaries = []
      this.setFilters(
        clone(documentListFiltersBoilerplate)
      )
    },
    hideSingleDocumentViewer() {
      this.hideModal(SINGLE_DOCUMENT_PREVIEW_MODAL_ID)
    },
    openDocumentPanel(document: GedDocument) {
      this.currentDocument = document
      this.displayDocumentSidebar = true
    },
    getDocumentPreview(
      document_id: GedDetailedDocument['document_id'],
      document_id_version: DocumentIdVersion
    ) {
      const {
        structure: { uuidData },
      } = this.getGedScope()
      const code_societe = this.currentDocument?.code_societe
        || this.getGedScope().current_company_code


      const options = {
        uuidData,
        code_societe,
        document_id,
        document_id_version,
      }

      return this.store_documents_preview.exec(options)
        .then((res: FileInformation) => {
          this.fileInformation = res
          this.showModal(SINGLE_DOCUMENT_PREVIEW_MODAL_ID)
        })
        .catch(error => {
          console.warn('getDocumentPreview', error)
          this.ToastBuilder
            .error(toString(error))
            .title(`Prévisualisation du document`)
            .show()
        })
    },
    loadSalarieOptions(search: string) {
      const {
        structure: { uuidData },
        company_code_list,
      } = this.getGedScope()

      const options = {
        search,
        uuidData,
        code_societes: company_code_list,
      }

      return this.store_salarie.search(options)
    },
    filterSalaries(salaries: GedSalarie[]) {
      return reject(
        (salarie: GedSalarie) => any(
          equals(salarie.salarie_id),
          map.iterable(
            prop('salarie_id'),
            this.selectedSalaries
          )
        ),
        salaries
      )
    },
    selectSalarie(salarie: GedSalarie) {
      const upd = concat(
        this.selectedSalaries,
        [ salarie ]
      )

      this.selectedSalaries = upd
      this.context.filters.salarie_id = map.iterable(
        prop('salarie_id'),
        upd
      )
    },
    removeSalarie(salarie: GedSalarie) {
      const upd = reject(
        (salarie_) => equals(salarie.salarie_id, salarie_.salarie_id),
        this.selectedSalaries
      )

      this.selectedSalaries = upd
      this.context.filters.salarie_id = map.iterable(
        prop('salarie_id'),
        upd
      )
    },
    downloadDocument(
      document_id: GedDetailedDocument['document_id'],
      version: DocumentIdVersion
    ) {
      const {
        structure: { uuidData },
      } = this.getGedScope()
      const code_societe = this.currentDocument?.code_societe
        || this.getGedScope().current_company_code

      const options = {
        uuidData,
        code_societe,
        document_id: document_id,
        document_id_version: version,
      }

      return this.store_documents_preview.exec(options)
        .then(res => this.store_documents_download.exec(res))
        .then(() => {
          this.ToastBuilder
            .success()
            .title(`Document téléchargé`)
            .show()
        })
        .catch(error => {
          console.warn('downloadDocument', error)

        switch (error.message) {
          case REMOTE_ERRORS.DOWNLOAD_FAILED:
          case ERRORS.DOWNLOAD_FAILURE:
            this.ToastBuilder
              .error("Le document n'a pas pu être téléchargé")
              .title(`Échec du téléchargement du document`)
              .show()
            break
          case ERRORS.BLOB_PARSING_FAILURE:
            this.ToastBuilder
              .error('Le document semble endommagé')
              .title(`Échec du téléchargement du document`)
              .show()
            break
          default:
            this.ToastBuilder
              .error(toString(error))
              .title(`Erreur lors du téléchargement du document`)
              .show()
        }
      })
    },
    downloadMultipleDocuments(documents: GedDocument[]) {
      const { structure: { uuidData } } = this.getGedScope()

      this.isDownloadPending = true

      const groupedByCompany = documents.reduce(
        (acc, document) => {
          const code = document.code_societe
          const list = acc[code] ?? []
          return {
            ...acc,
            [code]: [ ...list, document.document_id ]
          }
        },
        {}
      ) as Record<string, string[]>

      return Promise.all(
          Object.entries(groupedByCompany).map(([code_societe, document_ids]) => {
            const options = {
              uuidData,
              code_societe,
              document_ids,
            }
            return this.store_documents_download.multiple(options)
              .then(downloadFromChunks)
          })
        )
        .then(() => {
          this.ref_pending_icon?.onSuccess()
          this.isDownloadPending = false
        }).catch(error => {
          console.warn('downloadMultipleDocuments', error)
          this.isDownloadPending = false

          this.ToastBuilder
            .error(toString(error))
            .title(`Erreur lors du téléchargement des documents`)
            .show()
        })
    },
    showMultipleDocuments(documents: GedDocument[]) {
      this.displayMultiDocumentModal = true
      this.selectedDocuments = documents
    },
    fetch() {
      const {
        structure: { uuidData },
        company_code_list,
        current_company_code,
      } = this.getGedScope()
      const options = {
        uuidData,
        code_societe: current_company_code,
        code_societes: company_code_list,
      }

      if (!uuidData) {
        this.documentTypeOptions = []
        this.yearOptions = []
        return Promise.resolve(undefined)
      }

      return this.store_documents_list.filters(options)
        .then(({ types, years }) => {
          this.documentTypeOptions = types
          this.yearOptions = years
        })
        .catch(tapCatch(err => {
          console.warn('fetch', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Liste des documents`)
            .show()
        }))
    },
    getGedScope() {
      const currentLevel = this['fromCurrentLevel']()
      const structure = currentLevel.structure
      const company_code_list = Object.keys(structure.societes ?? {})

      if (currentLevel.kind === 'COMPANY') {
        return {
          structure,
          current_company_code: currentLevel.company.code_societe,
          company_code_list: [ currentLevel.company.code_societe ],
        }
      }

      return {
        structure,
        current_company_code: '',
        company_code_list,
      }
    },
  },
})
</script>
